import { useState } from "react"
import { KeyedMutator } from "swr"

import {
    SUPPORT_REFER,
    SUPPORT_REFER_HELP_DESK,
} from "../../../../errors/errorMessages"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { snackbar } from "../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../clients/clients"
import { TUsersOutput } from "../../mappers/adminPortal/activeUsersMapper"
import { DELETE_QUICKSIGHT_USER } from "../../urls/private"

type IDeleteQuicksightUser = {
    deleteQuicksightUser: () => Promise<void>
    isLoading: boolean
}

/**
 * useDeleteQuicksightUser is a custom hook that handles the deletion of a QuickSight user.
 * It deletes user account from ENA platform, IAM, Quicksight and Cognito.
 * It provides a function to delete the ena user and manages the loading state during the process.
 */
const useDeleteQuicksightUser = (
    email: string,
    fullName: string,
    mutate: KeyedMutator<TUsersOutput>
): IDeleteQuicksightUser => {
    const [isLoading, setIsLoading] = useState(false)
    const deleteQuicksightUser = async (): Promise<void> => {
        try {
            setIsLoading(true)
            const encodedEmail = encodeURIComponent(email)
            await deleteClientProtected(
                DELETE_QUICKSIGHT_USER(encodedEmail.toLowerCase())
            )
            snackbar(`${fullName} successfully deleted`, "success")
            mutate()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)

            let errorMsg
            if (errorObj?.code === 403) {
                errorMsg = `${errorObj?.message}. Please ${SUPPORT_REFER_HELP_DESK}`
            } else {
                errorMsg = `Unable to delete ${fullName}. Please try again. ${SUPPORT_REFER}`
            }

            snackbar(errorMsg, "warning")
        } finally {
            setIsLoading(false)
        }
    }

    return {
        deleteQuicksightUser,
        isLoading,
    }
}

export default useDeleteQuicksightUser
